// Provider Skill Assignments — admin UI for assigning skills to providers
// Shows two provider cards (Knowledge Base + Certant AI) with skill dropdowns

import { BookOpen, Brain, CheckCircle, Loader2, Save, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useToast } from '../../hooks/use-toast';
import {
  AiSkill,
  getAiSkills,
  getSkillAssignments,
  SkillAssignment,
  upsertSkillAssignment,
} from '../../services/aiSkillsService';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

type HealthStatus = {
  status: string;
  service: string;
  version: string;
  timestamp?: string;
  providers: {
    'knowledge-base'?: boolean;
    certant?: boolean;
  };
};

type ProviderConfig = {
  id: 'knowledge-base' | 'certant-ai';
  name: string;
  description: string;
  icon: typeof BookOpen;
};

const PROVIDERS: ProviderConfig[] = [
  {
    id: 'knowledge-base',
    name: 'Knowledge Base',
    description: 'RAG with uploaded documents — skill is injected into the system prompt',
    icon: BookOpen,
  },
  {
    id: 'certant-ai',
    name: 'Certant AI',
    description: 'GraphRAG — skill post-processes responses through an LLM',
    icon: Brain,
  },
];

export const ProviderSkillAssignments = () => {
  const { toast } = useToast();
  const [skills, setSkills] = useState<AiSkill[]>([]);
  const [assignments, setAssignments] = useState<SkillAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [health, setHealth] = useState<HealthStatus | null>(null);

  // Track local selection state per provider
  const [selections, setSelections] = useState<Record<string, string>>({
    'knowledge-base': '',
    'certant-ai': '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [skillsData, assignmentsData] = await Promise.all([
          getAiSkills(),
          getSkillAssignments(),
        ]);
        setSkills(skillsData);
        setAssignments(assignmentsData);

        // Initialize selections from current assignments
        const initial: Record<string, string> = { 'knowledge-base': '', 'certant-ai': '' };
        for (const a of assignmentsData) {
          initial[a.provider] = a.skill_id ?? '';
        }
        setSelections(initial);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load data';
        toast({ title: 'Error', description: message, variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    load();

    // Fetch provider health status
    fetch(`${API_BASE_URL}/api/charlie-chat/health`)
      .then((r) => r.json() as Promise<HealthStatus>)
      .then(setHealth)
      .catch(() => setHealth(null));
  }, []);

  const handleSave = async (providerId: 'knowledge-base' | 'certant-ai') => {
    setSaving(providerId);
    try {
      const skillId = selections[providerId] || null;
      await upsertSkillAssignment(providerId, skillId);

      // Refresh assignments
      const updated = await getSkillAssignments();
      setAssignments(updated);

      const skill = skills.find((s) => s.id === skillId);
      toast({
        title: 'Assignment saved',
        description: skill
          ? `"${skill.skill_name}" assigned to ${PROVIDERS.find((p) => p.id === providerId)?.name}`
          : `Skill removed from ${PROVIDERS.find((p) => p.id === providerId)?.name}`,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save assignment';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading assignments…</span>
      </div>
    );
  }

  const currentAssignment = (providerId: string) =>
    assignments.find((a) => a.provider === providerId);

  // Map provider id to health key
  const providerHealthKey = (providerId: string): boolean | undefined => {
    if (!health) return undefined;
    if (providerId === 'knowledge-base') return health.providers['knowledge-base'];
    if (providerId === 'certant-ai') return health.providers.certant;
    return undefined;
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold tracking-tight">Provider Skill Assignments</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Assign one skill per provider. When assigned, the skill modifies how the provider processes responses for your entire organisation.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {PROVIDERS.map((prov) => {
          const Icon = prov.icon;
          const current = currentAssignment(prov.id);
          const selectedId = selections[prov.id];
          const hasChanged = (current?.skill_id ?? '') !== selectedId;
          const isHealthy = providerHealthKey(prov.id);

          return (
            <div
              key={prov.id}
              className="border border-border rounded-lg p-4 space-y-3 bg-card"
            >
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-muted-foreground" />
                <div className="flex-1">
                  <p className="text-sm font-medium">{prov.name}</p>
                  <p className="text-xs text-muted-foreground">{prov.description}</p>
                </div>
                {health && (
                  <div className="flex items-center gap-1">
                    {isHealthy ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                    <span className={`text-xs ${isHealthy ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                      {isHealthy ? 'Online' : 'Offline'}
                    </span>
                  </div>
                )}
              </div>

              {current?.skill && (
                <div className="text-xs px-2 py-1 rounded bg-primary/10 text-primary inline-block">
                  Active: {current.skill.skill_name}
                </div>
              )}

              <div className="flex items-center gap-2">
                <select
                  value={selectedId}
                  onChange={(e) =>
                    setSelections((prev) => ({ ...prev, [prov.id]: e.target.value }))
                  }
                  className="flex-1 px-3 py-2 border border-border rounded-lg text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                >
                  <option value="">None — no skill</option>
                  {skills.length === 0 && (
                    <option disabled>No skills available — create one first</option>
                  )}
                  {skills.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.skill_name}
                      {s.tenant_id === null ? ' (Global)' : ''}
                    </option>
                  ))}
                </select>

                <button
                  onClick={() => handleSave(prov.id)}
                  disabled={!hasChanged || saving === prov.id}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving === prov.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Save
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {health && (
        <p className="text-xs text-muted-foreground">
          {health.service} v{health.version} · Last checked: {new Date(health.timestamp ?? Date.now()).toLocaleTimeString()}
        </p>
      )}

      {health && !health.providers.certant && (
        <div className="text-xs text-muted-foreground bg-muted/40 rounded-lg px-4 py-3 space-y-1">
          <p className="font-medium">To enable Certant AI, set these environment variables on the server:</p>
          <pre className="font-mono">
            {'CERTANT_API_URL=https://your-certant-instance\nCERTANT_KB_ID=your-kb-id\nCERTANT_API_KEY=your-api-key   # optional'}
          </pre>
        </div>
      )}
    </div>
  );
};
